import { getBaseThemeName, getTheme } from "@/features/branding";
import { facadesApi } from "@/store/services/facades";
import { FacadesBatchPostRequest, FacadesBatchPostResponse } from "@/store/services/facades/types";
import {
  RawSimpleStructuralResponse,
  SimpleStructuralRequest,
  SimpleStructuralResponse,
} from "@/store/services/facades/types/simple";
import { GEOCODING_ERRORS_FROM_API } from "@/utils/geocoding";

const HEADERS = {
  Authorization: `Basic ${process.env.NEXT_PUBLIC_FACADES_ABCDE_API_KEY}`,
};

export const simpleFacadesApi = facadesApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Retrieve Simple Structural risk bands.
     */
    getSimpleStructuralRiskBands: build.query<SimpleStructuralResponse, SimpleStructuralRequest>({
      query: (body) => {
        return {
          method: "POST",
          url: "structural/simple",
          headers: HEADERS,
          body,
        };
      },
      transformErrorResponse: (error) => {
        const errorData = error.data as {
          title: string;
          status: number;
          detail: string;
          type: string;
        };

        const { detail, title } = errorData;

        let errorMessage = "Something unexpected happened. Please try again.";
        const isGeocodingError = GEOCODING_ERRORS_FROM_API.find((err) => errorData.title === err);
        if (isGeocodingError) {
          errorMessage = `The system was unable to geocode this entry using the information provided. Please supply more detailed address information or, for the most accurate results, provide latitude and longitude coordinates.  For further guidance, please refer
                    [${
                      getBaseThemeName() === "hkma" ? `this geocode passthrough document` : `to this article`
                    }](${getTheme().meta.config.geocoding.guidelineDocumentUrl})`;
        } else if (title && detail) {
          errorMessage = `${title}. ${detail}.`;
        }

        return {
          message: errorMessage,
          status: errorData.status,
        };
      },
      transformResponse: (response: RawSimpleStructuralResponse): SimpleStructuralResponse => {
        return {
          mode: "simple_structural",
          ...response,
        };
      },
    }),

    /**
     * Send Comprehensive Structural batch items, determining endpoint by archetypes.
     */
    postComprehensiveStructuralBatch: build.query<FacadesBatchPostResponse, FacadesBatchPostRequest>({
      queryFn: async (body, api) => {
        try {
          const response: FacadesBatchPostResponse = {
            errors: 0,
            successes: 0,
            stamps: [],
            error_summary: [],
          };

          const action = simpleFacadesApi.endpoints.postComprehensiveStructuralActualBatch.initiate(body);
          const { data } = await api.dispatch(action);

          if (data) {
            response.errors += data.errors;
            response.successes += data.successes;
            response.stamps?.push(...(data.stamps || []));
            response.error_summary?.push(...(data.error_summary || []));
          }

          return {
            data: response,
          };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: `${error}`,
            },
          };
        }
      },
    }),

    /**
     * Send Simple Structural batch items, determining endpoint by archetypes.
     */
    postSimpleStructuralBatch: build.query<FacadesBatchPostResponse, FacadesBatchPostRequest>({
      queryFn: async (body, api) => {
        try {
          const response: FacadesBatchPostResponse = {
            errors: 0,
            successes: 0,
            stamps: [],
            error_summary: [],
          };

          const action = simpleFacadesApi.endpoints.postSimpleStructuralActualBatch.initiate(body);
          const { data } = await api.dispatch(action);

          if (data) {
            response.errors += data.errors;
            response.successes += data.successes;
            response.stamps?.push(...(data.stamps || []));
            response.error_summary?.push(...(data.error_summary || []));
          }

          return {
            data: response,
          };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: `${error}`,
            },
          };
        }
      },
    }),

    /**
     * Send Comprehensive Structural batch items.
     * NOTE: You mostly want to use postComprehensiveStructuralBatch instead, which calls this.
     */
    postComprehensiveStructuralActualBatch: build.query<FacadesBatchPostResponse, FacadesBatchPostRequest>({
      query: ({ priority, batch }) => {
        return {
          method: "POST",
          url: "structural/comprehensive/batch",
          headers: {
            ...HEADERS,
            "Content-Type": "application/x-ndjson",
          },
          params: { priority },
          body: batch.join("\n"),
        };
      },
    }),

    /**
     * Send Simple Structural batch items.
     * NOTE: You mostly want to use postSimpleStructuralBatch instead, which calls this.
     */
    postSimpleStructuralActualBatch: build.query<FacadesBatchPostResponse, FacadesBatchPostRequest>({
      query: ({ priority, batch }) => {
        return {
          method: "POST",
          url: "structural/simple/batch",
          headers: {
            ...HEADERS,
            "Content-Type": "application/x-ndjson",
          },
          params: { priority },
          body: batch.join("\n"),
        };
      },
    }),
  }),
});
